import { endOfDay, formatDate, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ListItemGroupItem } from 'shared/components/vxl/ListItemGroup/ListItemGroup';
import { VXLComponentId } from 'shared/renderer/types';
import { formatCurrency } from 'shared/utils/string/currency';

interface useCreateReviewListItemsProps {
  advanceAmount: string;
  feeAmount: string;
  dueDate: string;
  repaymentDate: string;
  autoPaymentMethod?: string;
}

export const useCreateReviewListItems = (
  data: useCreateReviewListItemsProps,
) => {
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'review',
  });

  const hasData =
    data &&
    !!data.advanceAmount &&
    !!data.feeAmount &&
    !!data.dueDate &&
    !!data.repaymentDate;

  if (!hasData) {
    return {
      reviewListItems: { items: [] },
      error: translatedText('error.noData'),
      fetching: false,
    };
  }

  const totalRepaymentAmountWithFee =
    parseFloat(data.advanceAmount) + parseFloat(data.feeAmount);

  const dueDate = formatDate(endOfDay(parseISO(data.dueDate)), 'MMM dd, yyyy');

  const paymentMethod = data.autoPaymentMethod
    ? {
        keyId: '4',
        primaryCopy: translatedText('autoPaymentMethod'),
        trailingAccessory: {
          id: '4',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: data.autoPaymentMethod,
          },
        },
        showsChevron: false,
      }
    : {};

  const sectionList = {
    items: [
      {
        keyId: '1',
        primaryCopy: translatedText('fee'),
        trailingAccessory: {
          id: '1',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: formatCurrency(data.feeAmount, true),
          },
        },
        showsChevron: false,
      },
      {
        keyId: '2',
        primaryCopy: translatedText('total'),
        trailingAccessory: {
          id: '2',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: formatCurrency(totalRepaymentAmountWithFee, true),
          },
        },
        showsChevron: false,
      },
      {
        keyId: '3',
        primaryCopy: translatedText('autoPaymentDateTitle'),
        trailingAccessory: {
          id: '3',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: data.repaymentDate,
          },
        },
        showsChevron: false,
      },
      ...(data.autoPaymentMethod ? [paymentMethod] : []),
      {
        keyId: '5',
        primaryCopy: `${translatedText('amountDueOn')} ${dueDate}`,
        trailingAccessory: {
          id: '5',
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: formatCurrency(totalRepaymentAmountWithFee, true),
          },
        },
        showsChevron: false,
      },
    ] as ListItemGroupItem[],
  };

  return {
    reviewListItems: sectionList,
    data: data,
    error: false,
  };
};
